import React from "react";
import {
	Font,
	Document,
	Page,
	Text,
	View,
	StyleSheet,
	Image,
} from "@react-pdf/renderer";
import moment from "moment";
import akLogo from "@/assets/akLogo.jpg";
import font from "@/assets/NotoSansSC-Regular.ttf";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { Button } from "antd";
import { DownloadOutlined } from "@ant-design/icons";

Font.register({
	family: "Noto Sans",
	src: font,
});

// Create styles
const styles = StyleSheet.create({
	page: {
		flexDirection: "column",
		backgroundColor: "#FDFDFD",
		padding: 20,
	},
	header: {
		paddingTop: 20,
		flexDirection: "row",
		justifyContent: "space-between",
		marginBottom: 10,
	},
	image: {
		width: 100,
		height: 100,
	},
	leftHeader: {
		fontSize: 11,
		color: "black",
		lineHeight: 1.5,
	},
	rightHeader: {
		fontSize: 18,
		justifyContent: "center",
		alignItems: "center",
	},
	infoSection: {
		flexDirection: "row",
		marginBottom: 10,
	},
	infoLeft: {
		flex: 3,
		fontSize: 15,
		color: "black",
		lineHeight: 1.5,
	},
	infoRight: {
		flex: 1,
		fontSize: 11,
		color: "black",
		lineHeight: 1.5,
		justifyContent: "center",
		alignItems: "center",
	},
	lightBackground: {
		flex: 1,
		backgroundColor: "#F5F5F5",
		color: "black",
		fontSize: 11,
		lineHeight: 1.5,
		justifyContent: "center",
		alignItems: "center",
	},
	dividerLine: {
		borderTop: "4px solid black",
		marginVertical: 10,
	},
	tableHeader: {
		paddingTop: 5,
		backgroundColor: "#F5F5F5",
		flexDirection: "row",
		borderBottomWidth: 1,
		borderColor: "#000",
		marginBottom: 5,
	},
	dateCell: {
		flex: 0.1,
		textAlign: "center",
		fontSize: 10,
		lineHeight: 1.5,
		alignItems: "center",
	},
	itemCell: {
		flex: 0.1,
		textAlign: "center",
		fontSize: 10,
		lineHeight: 1.5,
		alignItems: "center",
	},
	descCell: {
		flex: 0.4,
		textAlign: "center",
		fontSize: 10,
		lineHeight: 1.5,
		alignItems: "center",
	},
	unitCell: {
		flex: 0.1,
		textAlign: "center",
		fontSize: 10,
		lineHeight: 1.5,
		alignItems: "center",
	},
	taxCell: {
		flex: 0.1,
		textAlign: "center",
		fontSize: 10,
		lineHeight: 1.5,
		alignItems: "center",
	},
	amountCell: {
		flex: 0.2,
		textAlign: "center",
		fontSize: 10,
		lineHeight: 1.5,
		alignItems: "center",
	},
	tableRowCell: {
		flexDirection: "row",
		lineHeight: 1.5,
		marginBottom: 5,
		borderBottomWidth: 1,
		borderColor: "#A9A9A9",
	},
	footer: {
		position: "absolute",
		bottom: 30,
		left: 0,
		right: 0,
		textAlign: "center", // Center-align the text
		fontSize: 12, // Set font size to 12
	},
});

const renderDate = (text) => {
	return moment(text).format("DD/MM/YYYY");
};

const renderDueDate = (text) => {
	const originalDate = moment(text);
	return originalDate.add(30, "day").format("DD/MM/YYYY");
};

const renderTravelDate = (text) => {
	return moment(text).format("DD/MM");
};

// Create Document Component
const MyDocument = ({ data }) => (
	<Document>
		<Page size="A4" style={styles.page}>
			{/* Header Section */}
			<View style={styles.header}>
				<View style={styles.leftHeader}>
					<Text
						style={{
							fontFamily: "Helvetica-Bold",
							color: "red",
							fontSize: 16,
						}}
					>
						A&K TRAVEL AUSTRALIA PTY LTD
					</Text>
					<Text>22-30 WALLANCE AVE</Text>
					<Text>Phone: 0426438867</Text>
					<Text>aktravel@qq.com</Text>
					<Text>ABN: 85 625 028 542</Text>
				</View>
				<View style={styles.rightHeader}>
					<Image src={akLogo} style={styles.image} />
				</View>
			</View>

			{/* Info Section */}
			<View style={styles.infoSection}>
				<View style={styles.infoLeft}>
					<Text style={{ fontFamily: "Helvetica-Bold" }}>
						Tax invoice
					</Text>
				</View>

				<View style={styles.infoRight}>
					<Text style={{ fontFamily: "Helvetica-Bold" }}>
						Invoice number
					</Text>
					<Text>
						AK
						{data.year +
							"-" +
							data.month +
							"-" +
							data.customer_id.toString().padStart(3, "0")}
					</Text>
				</View>

				<View style={styles.infoRight}>
					<Text style={{ fontFamily: "Helvetica-Bold" }}>
						Issue date
					</Text>
					<Text>
						{renderDate(data.year + "-" + data.month + "-01")}
					</Text>
				</View>

				<View style={styles.lightBackground}>
					<Text style={{ fontFamily: "Helvetica-Bold" }}>
						Due date
					</Text>
					<Text>
						{renderDueDate(data.year + "-" + data.month + "-01")}
					</Text>
				</View>
			</View>

			<View style={styles.leftHeader}>
				<Text style={{ fontFamily: "Helvetica-Bold" }}>Bill to</Text>
				<Text>{data.customer_name}</Text>
			</View>

			{/* Divider Line */}
			<View style={styles.dividerLine} />

			{/* Body Section */}
			<View style={styles.tableHeader}>
				<View style={styles.dateCell}>
					<Text style={{ fontFamily: "Helvetica-Bold" }}>Date</Text>
				</View>
				<View style={styles.itemCell}>
					<Text style={{ fontFamily: "Helvetica-Bold" }}>
						Item ID
					</Text>
				</View>
				<View style={styles.descCell}>
					<Text style={{ fontFamily: "Helvetica-Bold" }}>
						Description
					</Text>
				</View>
				<View style={styles.unitCell}>
					<Text style={{ fontFamily: "Helvetica-Bold" }}>Units</Text>
				</View>
				<View style={styles.taxCell}>
					<Text style={{ fontFamily: "Helvetica-Bold" }}>Tax</Text>
				</View>
				<View style={styles.amountCell}>
					<Text style={{ fontFamily: "Helvetica-Bold" }}>
						Amount($)
					</Text>
					<Text>including tax</Text>
				</View>
			</View>

			{/* Table Body */}
			{data.data &&
				data.data.map((item, index) => (
					<View key={index} style={styles.tableRowCell}>
						<View style={styles.dateCell}>
							<Text>{renderTravelDate(item.travel_date)}</Text>
						</View>
						<View style={styles.itemCell}>
							<Text>{item.product_id}</Text>
						</View>
						<View style={styles.descCell}>
							<Text style={{ fontFamily: "Noto Sans" }}>
								{item.id}
							</Text>
							<Text style={{ fontFamily: "Noto Sans" }}>
								{item.product_title}
							</Text>
							<Text style={{ fontFamily: "Noto Sans" }}>
								{item.option}
							</Text>
						</View>
						<View style={styles.unitCell}>
							<Text style={{ fontFamily: "Noto Sans" }}>
								{item.people_no}
							</Text>
						</View>
						<View style={styles.taxCell}>
							<Text>GST</Text>
						</View>
						<View style={styles.amountCell}>
							<Text>
								{parseFloat(item.total_price).toFixed(2)}
							</Text>
						</View>
					</View>
				))}

			{/* Footer Section */}
			<View style={styles.footer}></View>
		</Page>
	</Document>
);

const DownloadButton = ({ data }) => {
	if (data == null) return <></>;
	return (
		<PDFDownloadLink
			style={{
				width: "150px",
			}}
			document={<MyDocument data={data} />}
			fileName={
				"AK" +
				data.year +
				"-" +
				data.month +
				"-" +
				data.customer_id.toString().padStart(3, "0") +
				".pdf"
			}
		>
			{({ blob, url, loading, error }) => (
				<Button
					icon={<DownloadOutlined />}
					size="large"
					className="edit-customer-details-button"
					style={{
						backgroundColor: "deepskyblue",
						color: "white",
					}}
				>
					{loading ? "Loading..." : "Print"}
				</Button>
			)}
		</PDFDownloadLink>
	);
};

export { MyDocument, DownloadButton };
